import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { Group } from "@domain/group/group";
import { Workspace } from "@domain/group/workspace/workspace";
import { Clamp } from "@ui/components/containers/clamp";
import { Primary, PrimaryBold } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { ChapterIcon, HatIcon, QuizIcon } from "@ui/components/icons";
import { Row, row, Stack } from "@ui/components/layout";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { useModalTools } from "@ui/modals/modalContext";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { workspaceColors } from "@ui/styles/colors";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { WorkspaceEditor } from "./workspaceEditor";
import { Illustrations } from "./workspaceIllustration";

interface WorkspaceThumbnailProps extends React.HTMLAttributes<HTMLDivElement> {
	shareroom: Group;
	workspace: Workspace;
}
export const WorkspaceThumbnail: React.FC<WorkspaceThumbnailProps> = ({ workspace, shareroom, ...htmlProps }) => {
	const { createModal } = useModalTools();
	const { workspaceService } = useServices();

	const askConfirmation = useConfirmation(
		I18n.t("workspace.delete.title"),
		I18n.t("delete"),
		I18n.t("workspace.delete.message")
	);

	const index = workspace.id.charCodeAt(0) % 3;

	return (
		<Container {...htmlProps}>
			<Link to={Path.Shareroom(shareroom, "workspaces", workspace.id)}>
				<TopSide background={workspace.color ?? workspaceColors[0]}>
					{Illustrations[index]}
					<WorkspaceTitle size={1}>{workspace.name}</WorkspaceTitle>
				</TopSide>
				<BottomSide>
					<Stack gap={10}>
						<Row gap={40} justify="flex-start" align="center">
							<WorkspaceData>
								<HatIcon />
								<div>{I18n.t("workspace.courses.count", { count: workspace.courseCount })}</div>
							</WorkspaceData>
							<WorkspaceData>
								<QuizIcon />
								<div>{I18n.t("workspace.quizzes.count", { count: workspace.quizCount })}</div>
							</WorkspaceData>
						</Row>
						<Row gap={40} justify="flex-start" align="center">
							<WorkspaceData>
								<QuizIcon />
								<div>{I18n.t("workspace.articles.count", { count: workspace.articleCount })}</div>
							</WorkspaceData>
							<WorkspaceData>
								<ChapterIcon />
								<div>{I18n.t("workspace.assets.count", { count: workspace.assetCount })}</div>
							</WorkspaceData>
						</Row>
					</Stack>
					<Clamp>{workspace.objective}</Clamp>
				</BottomSide>
			</Link>
			{shareroom.userRole === "ADMINISTRATOR" && (
				<StyledMenu blur>
					<SimpleMenuContent
						onEdit={() =>
							createModal((close) => (
								<WorkspaceEditor close={close} initialWorkspace={workspace} shareroom={shareroom} />
							))
						}
						onDelete={
							workspace.isDefault
								? undefined
								: () =>
										askConfirmation(async () => {
											try {
												await workspaceService.delete(workspace.id, shareroom.id);
												createToast(I18n.t("success"), I18n.t("workspace.delete.success"), "success");
											} catch {
												createToast(I18n.t("error"), I18n.t("workspace.delete.error"), "error");
											}
										})
						}
					/>
				</StyledMenu>
			)}
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	position: relative;
	overflow: hidden;

	> a  {
		display: block;
		height: 100%;
		> div  {
			flex: 1;
		}
	}
`;

const TopSide = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "background",
})<{ background: string }>`
	position: relative;
	overflow: hidden;
	background-color: ${({ background }) => background};
	padding: 30px 80px 30px 20px;
	${({ theme }) => theme.textStyles.subtitle};
`;

const WorkspaceTitle = styled(Clamp)`
	position: relative;
`;
const BottomSide = styled(Primary)`
	padding: 30px 20px;
	${gap(30)};
`;

const WorkspaceData = styled(PrimaryBold)`
	${row(14, "flex-start", "center")};

	svg {
		path {
			fill: ${({ theme }) => theme.colors.textPrimary};
		}
	}
`;

const StyledMenu = styled(KebabMenu)`
	position: absolute;
	right: 10px;
	top: 10px;
`;
